/**
 * LIFECYCLE ACTIONS AND THEIR REFUSALS → HEBREW. 08 §6.0 D11, OA-0001 §3.11, DEV-0011.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * "REFUSED" IS NOT AN ANSWER. "IT IS ALREADY SUSPENDED" IS.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `refuse()` in `routes/commercial.ts` puts `currentStatus` and `permittedFrom` on the body, and the
 * company lifecycle route puts `from` and `allowedTransitions` on its own — described there as *"the
 * actionable half: a screen can offer the three buttons that work instead of printing refused"*.
 *
 * This module turns those into sentences. It is the difference between an operator who now knows the
 * job is already done and an operator who opens a support ticket about a working product.
 *
 * ── THE TWO REFUSALS THAT MUST NOT COLLAPSE ─────────────────────────────────────────────────────
 *
 * A SUSPENDED customer and a CLOSED one are not the same state and not the same conversation.
 *
 *   suspended — commercial enforcement. 08 §6.0 D11 makes it deny every action class, AND
 *               OA-0001 §3.11 makes it destroy nothing: every company, every membership and every
 *               grant is exactly where it was, and reinstatement is one action. The customer is
 *               still listed in the console precisely because they have to be findable to reinstate.
 *   closed    — the commercial relationship is over. It is not a deletion either (02 §8: no physical
 *               delete), but it is not a state a screen should offer to undo in one click, and the
 *               sentence says which of the two the operator is looking at.
 *
 * A product that told an operator "מושהה/סגור — הפעולה נדחתה" for both would be answering a question
 * nobody asked.
 *
 * ── AND WHY AN UNKNOWN STATUS IS A MALFORMED REQUEST, NOT A REFUSED TRANSITION ──────────────────
 *
 * The company lifecycle route parses the target status at the boundary and answers 400, not 409, for
 * one the product does not have. DEV-0011 is the reason: a 409 would tell a client that `archived` is
 * a state they LACK PERMISSION to reach, when the product has no such state at all. There is
 * therefore no Hebrew here for a status outside the enum, and the screen never offers one — it builds
 * its buttons from `allowedTransitions`, which the server produced.
 *
 * Pure. Every verdict arrived already made. Nothing here evaluates whether a move is legal.
 */

import type {
  CompanyLifecycleStatus,
  CompanyTransitionRefusal,
  SubscriptionState,
  TenantLifecycleRefusal,
  TenantStatus,
} from './contract';
import { subscriptionStateHe, tenantStatusHe } from './adminVocabulary';

// ── The actions themselves ────────────────────────────────────────────────────────────────────────

/**
 * The three tenant lifecycle verbs the Control Plane can perform, plus what each one does NOT do.
 *
 * The second half of every blast radius is the half an operator is afraid of, and it is stated: none
 * of these touches a company, a membership or a company grant. OA-0001 §3.11 — *"commercial
 * enforcement must not corrupt or delete existing Company data"* — is a promise the screen has to
 * make at the moment the button is pressed, not a paragraph in a contract.
 */
export const TENANT_LIFECYCLE_ACTIONS = ['suspend', 'reactivate', 'close'] as const;
export type TenantLifecycleAction = (typeof TENANT_LIFECYCLE_ACTIONS)[number];

export interface LifecycleActionText {
  /** A VERB, in office Hebrew — 09 §1.3, and `<Modal>`'s `intentHe` is required to be one. */
  readonly intentHe: string;
  /** 09 §2.2: what will happen, including what will NOT. Never left for the operator to infer. */
  readonly blastRadiusHe: string;
  readonly confirmLabelHe: string;
  /** 09 §2.2: an irreversible action is re-selected explicitly and is never the default focus. */
  readonly irreversible: boolean;
  /** Whether the server requires a written reason. It is recorded in the audit row beside the actor. */
  readonly requiresReason: boolean;
}

const TENANT_ACTION_HE: Readonly<Record<TenantLifecycleAction, LifecycleActionText>> = {
  suspend: {
    intentHe: 'השעיית לקוח',
    blastRadiusHe:
      'ההשעיה חוסמת את כל הפעולות בארגון הזה עד לביטולה. אף חברה, אף משתמש ואף הרשאה אינם נמחקים ' +
      'ואינם משתנים, והארגון ממשיך להופיע ברשימת הלקוחות כדי שאפשר יהיה להחזירו.',
    confirmLabelHe: 'השעיה',
    irreversible: false,
    requiresReason: true,
  },
  reactivate: {
    intentHe: 'ביטול השעיה',
    blastRadiusHe:
      'ביטול ההשעיה מחזיר את הארגון למצב פעיל. הנתונים לא השתנו בזמן ההשעיה, ולכן אין מה לשחזר — ' +
      'הפעולות פשוט נפתחות שוב.',
    confirmLabelHe: 'ביטול השעיה',
    irreversible: false,
    requiresReason: true,
  },
  close: {
    intentHe: 'סגירת לקוח',
    blastRadiusHe:
      'סגירה מסיימת את ההתקשרות המסחרית וחוסמת את כל הפעולות בארגון. הנתונים אינם נמחקים — אין ' +
      'מחיקה פיזית במערכת — אך הפתיחה מחדש אינה פעולה של לחיצה אחת, והיא נעשית מול הבעלים של ' +
      'הפלטפורמה.',
    confirmLabelHe: 'סגירת הלקוח',
    // Not "destructive" in the data sense and still not a click to be taken back: the relationship is
    // what ends, and re-opening it is a commercial decision rather than an undo.
    irreversible: true,
    requiresReason: true,
  },
};

export function tenantLifecycleActionText(action: TenantLifecycleAction): LifecycleActionText {
  return TENANT_ACTION_HE[action];
}

// ── Tenant lifecycle refusals ─────────────────────────────────────────────────────────────────────

export interface LifecycleRefusalText {
  readonly titleHe: string;
  readonly explanationHe: string;
  readonly nextStepHe: string;
  readonly tone: 'warn' | 'error';
  /** Nothing was written. Stated every time, because a half-applied lifecycle move is what is feared. */
  readonly unchangedHe: string;
}

const UNCHANGED_HE = 'לא נשמר שינוי. מצב הלקוח נשאר כפי שהיה לפני הבקשה.';

/**
 * The reason codes `refuse()` can carry that this build has words for.
 *
 * A code with no entry falls through to `GENERIC`, which says the two things that are always true —
 * the server refused and nothing changed — and names the current status if one arrived. It never
 * prints the code (09 §9.1 V3).
 */
const TENANT_REFUSAL_HE: Readonly<
  Record<
    string,
    {
      readonly titleHe: string;
      readonly explanationHe: string;
      /**
       * THE NEXT STEP, WHERE THE GENERIC TRAILER WOULD BE FALSE. **X8.** Optional, and read in exactly
       * one place below.
       *
       * The derived trailer is *"refresh the customer card and look at its current state before
       * trying again"*, which is true for a refusal about a state that can change. It is FALSE for a
       * permanent one: a refusal that will answer the same way after every refresh must not send an
       * operator round a loop that cannot succeed. A row carrying this field knows better than the
       * derivation, so it wins.
       */
      readonly nextStepHe?: string;
    }
  >
> = {
  tenant_not_found: {
    titleHe: 'הלקוח אינו זמין',
    explanationHe: 'לא הצלחנו להציג את הלקוח שאליו מפנה הפעולה.',
  },
  already_in_state: {
    titleHe: 'הלקוח כבר במצב הזה',
    explanationHe: 'הפעולה שביקשת כבר בוצעה קודם לכן, ולכן אין מה לשנות.',
  },
  reason_required: {
    titleHe: 'חסרה סיבה לפעולה',
    explanationHe: 'פעולת מחזור חיים נרשמת ביומן יחד עם הסיבה שלה, ולכן היא אינה מתבצעת בלעדיה.',
  },
  subscription_not_found: {
    titleHe: 'ללקוח אין מנוי',
    explanationHe: 'הפעולה חלה על מנוי, ולא נמצא מנוי ללקוח הזה.',
  },
  self_approval_not_permitted: {
    titleHe: 'לא ניתן לאשר בקשה שהוגשה בידיך',
    explanationHe:
      'הפעולה הזו דורשת שני אנשים: מי שמבקש ומי שמאשר. אישור עצמי היה מבטל את הבקרה הכפולה.',
  },
  /*
   * THE NINE COMMERCIAL 409s — X7, PRODUCT-EXPECTATION_GLPOINT-S4W2-X7-FINAL-COPY.md §2.
   *
   * Every one of these is produced by `services/commercial.ts` and rendered
   * `GENERIC_TENANT_REFUSAL` — true, and unactionable. The classification is the whole item, and it
   * is carried by the PRESENCE OR ABSENCE of `nextStepHe`: one of the nine is transient and
   * deliberately has none, so the derived refresh trailer — the correct instruction for a state that
   * can still change — stands. The other eight are permanent, each in one of three materially
   * different ways (a no-op, a wrong input, a platform-configuration state), and for them the trailer
   * is FALSE: it would send an operator round a loop that cannot succeed.
   *
   * Omitting `nextStepHe` is therefore a positive statement, not an oversight.
   */

  /** TRANSIENT — and the ONLY one of the nine with no `nextStepHe`. The trailer is correct here. */
  concurrent_modification: {
    titleHe: 'שורת המנוי השתנתה בזמן הפעולה',
    explanationHe:
      'בין הרגע שהמסך נטען לרגע השליחה שונה המנוי של הלקוח — בחלון אחר או בידי אדם אחר — ולכן הבקשה לא הוחלה, כדי שלא תדרוס שינוי שנעשה במקביל.',
  },
  /**
   * PERMANENT UNTIL A DATE — 08 §5.3: a plan downgrade takes effect *"at period end only"*.
   *
   * The refusal carries the day, and the copy says WHY the rule exists rather than only that it
   * does: a period that was part at one plan and part at a lower one is a period whose price
   * nobody agreed.
   */
  downgrade_at_period_end: {
    titleHe: 'מעבר לתכנית מצומצמת חל מתחילת תקופה',
    explanationHe:
      'התכנית המבוקשת מצמצמת לפחות מגבלה אחת בהשוואה לתכנית הנוכחית. לפי כללי החיוב שאושרו, מעבר כזה נכנס לתוקף בתחילת תקופת חיוב ואינו מפצל תקופה קיימת בין שתי תכניות — התקופה הנוכחית נשארת במחירה עד סופה.',
    nextStepHe:
      'ניתן לבצע את המעבר ביום הראשון של התקופה הבאה, המוצג כאן. עד אז התכנית הנוכחית נשארת בתוקף.',
  },
  /**
   * PERMANENT UNTIL THE CUSTOMER ACTS — 08 §5.3's *"blocked"* row. The refusal names each
   * dimension, its limit and the current count, so the instruction below has something to point at.
   */
  downgrade_below_usage: {
    titleHe: 'השימוש הנוכחי גבוה מהמגבלות של התכנית המבוקשת',
    explanationHe:
      'התכנית המבוקשת מגבילה לפחות מדד אחד מתחת למה שהלקוח מחזיק כבר — מספר החברות, המשתמשים או המודולים המשוייכים. המערכת אינה מוחקת ואינה משביתה נתונים קיימים בעקבות שינוי מסחרי, ולכן המעבר נחסם במקום להשאיר את הלקוח מעל המגבלה.',
    nextStepHe:
      'יש להקטין את המדדים המפורטים כאן עד למגבלת התכנית המבוקשת, ואז לבצע את המעבר.',
  },
  /**
   * PERMANENT — and it is a GUARD rather than a state anybody can reach today: every seeded limit
   * code has a capacity kind. If it is ever seen, the product is metering something it cannot
   * count, and the honest answer is to say so rather than to assume the office holds none of it.
   */
  limit_not_countable: {
    titleHe: 'התכנית המבוקשת מגבילה מדד שאינו נמדד',
    explanationHe:
      'התכנית המבוקשת מגבילה מדד שהמערכת אינה יודעת לספור, ולכן לא ניתן לקבוע אם הלקוח מעל המגבלה. הנחה שאין לו מהמדד הזה כלום הייתה עלולה לשלול ממנו יכולת שהוא משתמש בה בפועל, ולכן המעבר נחסם.',
    nextStepHe:
      'רענון לא ישנה את התוצאה. יש לפנות לפיתוח עם קוד המגבלה המוצג כאן.',
  },
  /**
   * ── THE TWO REFUSALS OF THE SELF-SERVE PLAN CHANGE. **ITEM 4, 2026-11-03.** ─────────────────
   *
   * PERMANENT, BOTH. A refresh answers the same way, so neither gets the derived *"look at the
   * current state and try again"* trailer — that would send somebody round a loop that cannot
   * succeed.
   *
   * `negotiated_terms` IS NOT A REFUSAL TO ACT; IT IS A DIFFERENT PATH. *"אין לבטל או להחליף
   * תנאי מוסכם בשקט"* — a plan move resolves a new price, and an office holding an agreed term
   * has something the resolution could quietly stop applying. 08 §12B.3 row 17 calls the request
   * path *"the ONLY path for a grandfathered contract"*, and the next step names it.
   */
  negotiated_terms: {
    titleHe: 'לארגון יש תנאים מסחריים מוסכמים',
    explanationHe:
      'שינוי תכנית מחשב מחיר מחדש, ועלול לבטל תנאי שהוסכם עם הארגון. תנאי מוסכם אינו מוחלף בשקט, ולכן המעבר אינו נעשה בשירות עצמי.',
    nextStepHe:
      'רענון לא ישנה את התוצאה. יש לפתוח בקשת שינוי, והתנאים נבדקים יחד עם המעבר.',
  },
  /** PERMANENT — the plan is not on the published list, so no refresh will put it there. */
  plan_not_public: {
    titleHe: 'התכנית אינה מוצעת',
    explanationHe:
      'התכנית שנבחרה אינה ברשימת התכניות המפורסמות, ולכן לא ניתן לעבור אליה בשירות עצמי.',
    nextStepHe: 'רענון לא ישנה את התוצאה. יש לבחור תכנית מתוך הרשימה המוצגת.',
  },
  /** PERMANENT — a no-op. */
  plan_unchanged: {
    titleHe: 'התכנית המבוקשת היא התכנית הנוכחית',
    explanationHe:
      'הלקוח נמצא כבר על התכנית הזו. רישום "שינוי" בלי שינוי אמיתי היה מוסיף ליומן שורה שלא קרה בה דבר, ולכן הבקשה נדחתה.',
    nextStepHe:
      'רענון לא ישנה את התוצאה. אם התכנית אינה זו שהתכוונת אליה — יש לבחור תכנית אחרת.',
  },
  /**
   * PERMANENT — a no-op. The closing clause points at the `permittedStates` line the screen already
   * renders separately (`PlatformSubscriptionScreen.tsx:289, 296-298`) rather than competing with it.
   */
  state_unchanged: {
    titleHe: 'המנוי כבר במצב המבוקש',
    explanationHe: 'המצב שביקשת הוא המצב הנוכחי של המנוי, ולכן אין מה לשנות.',
    nextStepHe:
      'רענון לא ישנה את התוצאה. אם נדרש מצב אחר — יש לבחור אותו מתוך המצבים האפשריים המוצגים כאן.',
  },
  /** PERMANENT — already done. */
  discount_already_approved: {
    titleHe: 'ההנחה אושרה כבר',
    explanationHe:
      'להנחה הזו רשום כבר אישור, ולכן אין אישור שני לתת. האישור הקיים נשאר כפי שהוא.',
    nextStepHe:
      'רענון לא ישנה את התוצאה: הנחה שאושרה אינה ממתינה לאישור. שינוי התנאים נעשה בבקשת הנחה חדשה.',
  },
  /** PERMANENT — a dead object. */
  discount_revoked: {
    titleHe: 'ההנחה בוטלה ואינה ניתנת לאישור',
    explanationHe:
      'הבקשה להנחה הזו בוטלה לפני שאושרה, ולכן אי אפשר לאשר אותה. הביטול נשאר רשום ביומן.',
    nextStepHe:
      'רענון לא ישנה את התוצאה. אם ההנחה עדיין נדרשת — יש להגיש בקשת הנחה חדשה.',
  },
  /**
   * PERMANENT for this person. Audience note: this is the platform console, read by a platform
   * principal about their own estate — the same audience that reads `self_approval_not_permitted`
   * above. 09 §9.1 V3 governs the refused CUSTOMER's screen, not this one; no code is printed either
   * way.
   */
  not_an_eligible_approver: {
    titleHe: 'אינך המאשר שנקבע להנחה הזו',
    explanationHe:
      'כל הנחה כפופה למדיניות אישור שקובעת מי מאשר אותה, והמדיניות שחלה על ההנחה הזו מחייבת מאשר אחר. הבקשה נשארה ממתינה.',
    nextStepHe:
      'רענון לא ישנה את התוצאה. האישור יינתן בידי מי שהמדיניות קבעה, והבקשה ממתינה עד אז.',
  },
  /**
   * PERMANENT, AND NOT A CUSTOMER STATE AT ALL — platform configuration drift.
   *
   * `commercial.ts:1215-1220` states the rule in its own words: *"NO MATCHING POLICY IS A DENY. It
   * never means 'unconstrained'"*, and AP-2 makes a money-affecting capability with no governing row
   * a BOOT failure — so reaching this branch means the database drifted AFTER boot. Nothing on the
   * customer's card can fix it, which is why this is the one row whose next step sends the operator
   * OFF the card. The generic trailer does the exact opposite.
   */
  no_governing_approval_policy: {
    titleHe: 'אין מדיניות אישור שחלה על ההנחה הזו',
    explanationHe:
      'הנחה נרשמת רק תחת מדיניות אישור שמגדירה מי מאשר אותה. לא נמצאה מדיניות שחלה על הבקשה הזו, ולכן היא נדחתה. היעדר מדיניות אינו היתר.',
    nextStepHe:
      'רענון לא ישנה את התוצאה, והבעיה אינה בכרטיס הלקוח אלא בהגדרות הפלטפורמה. יש לפנות לבעלים של הפלטפורמה לפני ניסיון נוסף.',
  },
  /**
   * PERMANENT until a different action is taken. Verified not to promise a control that does not
   * exist: *set* and *extend* are two separate buttons calling two separate capabilities
   * (`PlatformSubscriptionScreen.tsx:536-537, 568-573`; `commercial.ts:926-931`).
   */
  no_trial_to_extend: {
    titleHe: 'אין תקופת ניסיון להארכה',
    explanationHe:
      'הארכה מוסיפה זמן לתקופת ניסיון שרשומה למנוי. למנוי הזה לא רשומה תקופת ניסיון, ולכן אין מה להאריך.',
    nextStepHe:
      'רענון לא ישנה את התוצאה. פתיחת תקופת ניסיון היא פעולה אחרת — קביעת תקופת ניסיון — ואינה הארכה.',
  },
  /**
   * PERMANENT for this input. The closing clause is true at this head: the trial end date is on the
   * card (`PlatformSubscriptionScreen.tsx:349`). The server also sends `currentTrialEndsAt` on this
   * refusal and the screen drops it — reading it would let the sentence NAME the date. That is a
   * recorded RECOMMENDATION, deferred by the Team Lead, and deliberately not done here.
   */
  trial_not_extended: {
    titleHe: 'התאריך המבוקש אינו מאריך את תקופת הניסיון',
    explanationHe:
      'הארכה חייבת להזיז את תאריך הסיום קדימה, והתאריך שנשלח אינו מאוחר מתאריך הסיום הקיים.',
    nextStepHe:
      'רענון לא ישנה את התוצאה. יש לבחור תאריך מאוחר מתאריך הסיום הנוכחי, המוצג בכרטיס המנוי.',
  },
  /*
   * THE SHARED PLAN. **X8, AMEND-3 §C-4 — `plan_limit` CONTAINED, not closed.**
   *
   * `billing.plan_limit` is keyed on the plan, so a limit written from this customer's card would
   * change the capacity of every other customer on the same plan. The store refuses it inside the
   * transaction. This is the only refusal in this map that carries its OWN `nextStepHe`, because it is
   * the only one that is permanent: refreshing cannot make a plan unshared, and the derived trailer
   * would have told the operator to try again on a path that can never succeed.
   */
  plan_shared_by_other_subscriptions: {
    titleHe: 'המכסה הזו משותפת ליותר ממנוי אחד',
    explanationHe:
      'המכסה נשמרת על התכנית ולא על הלקוח, ולתכנית הזו יש מנוי נוסף מלבד זה של הלקוח הזה — שינוי כאן היה משנה גם את המכסה של לקוח אחר, ולכן הוא נדחה.',
    nextStepHe:
      'רענון לא ישנה את התוצאה: כל עוד לתכנית יש מנוי נוסף, המכסה אינה ניתנת לשינוי.',
  },
};

const GENERIC_TENANT_REFUSAL: {
  readonly titleHe: string;
  readonly explanationHe: string;
  readonly nextStepHe?: string;
} = {
  titleHe: 'הפעולה לא בוצעה',
  explanationHe: 'השרת סירב לבקשה ולא נשמר שינוי.',
  // NO `nextStepHe`, deliberately: a code this build has no words for is one whose permanence is
  // unknown, so the derived trailer is the honest step for it.
};

/** Exposed for the mirror test only. */
export function mappedTenantRefusalReasons(): readonly string[] {
  return Object.keys(TENANT_REFUSAL_HE);
}

/**
 * A refusal, with the CURRENT state and the permitted origins spelled out where the server sent them.
 *
 * The next step is built from `permittedFrom` rather than being a fixed sentence, because "suspension
 * is permitted from פעיל or תקופת ניסיון" is an instruction and "the transition is invalid" is not.
 */
export function tenantLifecycleRefusalText(refusal: TenantLifecycleRefusal): LifecycleRefusalText {
  const copy = TENANT_REFUSAL_HE[refusal.reason] ?? GENERIC_TENANT_REFUSAL;
  const current =
    refusal.currentStatus === null
      ? null
      : `מצב הלקוח כעת: ${tenantStatusHe(refusal.currentStatus).labelHe}.`;
  const permitted =
    refusal.permittedFrom.length === 0
      ? null
      : `הפעולה אפשרית מהמצבים: ${refusal.permittedFrom.map((s) => tenantStatusHe(s).labelHe).join(' · ')}.`;

  return {
    titleHe: copy.titleHe,
    explanationHe: [copy.explanationHe, current].filter((s): s is string => s !== null).join(' '),
    /*
     * THE ROW'S OWN STEP FIRST, THEN THE DERIVED ONE, THEN THE TRAILER. **X8.**
     *
     * The order is the correction. With no permitted transitions the trailer *"refresh and look at the
     * current state"* was reached by EVERY refusal that had no state list — including permanent ones,
     * where it is false. A refusal that declares its own next step is making a statement the
     * derivation cannot make, so it outranks both.
     */
    nextStepHe:
      copy.nextStepHe ??
      permitted ??
      'אפשר לרענן את כרטיס הלקוח ולראות את מצבו העדכני לפני ניסיון נוסף.',
    tone: refusal.reason === 'already_in_state' ? 'warn' : 'error',
    unchangedHe: UNCHANGED_HE,
  };
}

/** The same shape for a forced subscription state change, whose permitted set is a state list. */
export function subscriptionStatesHe(states: readonly SubscriptionState[]): string {
  return states.map((s) => subscriptionStateHe(s).labelHe).join(' · ');
}

// ── Company lifecycle ─────────────────────────────────────────────────────────────────────────────

/**
 * The eight lifecycle states a company can be in, as office Hebrew — and `<LifecycleChip>` already
 * renders them. What this adds is the VERB for moving INTO each one, which is a different word:
 * a company is not "made dormant", the office "מעביר לתרדמת". 09 §1.3 again — a verb is an action.
 */
const TRANSITION_VERB_HE: Readonly<Record<CompanyLifecycleStatus, string>> = {
  prospect: 'החזרה למצב לקוח פוטנציאלי',
  active: 'החזרה לפעילות',
  dormant: 'העברה לתרדמת',
  former_client: 'סימון כלקוח לשעבר',
  merged_into: 'סימון כמוזגה לחברה אחרת',
  quarantined: 'העברה להסגר',
  source_unavailable: 'סימון שמקור הנתונים אינו זמין',
  review_required: 'סימון שנדרשת בדיקה',
};

export function companyTransitionVerbHe(to: CompanyLifecycleStatus): string {
  return TRANSITION_VERB_HE[to];
}

/** Exposed for the mirror test only — a status with no verb is a button with no label. */
export function mappedTransitionTargets(): readonly string[] {
  return Object.keys(TRANSITION_VERB_HE);
}

/**
 * `merged_into` is the one transition that CANNOT be expressed without a second company, and the
 * server's input union cannot be built without one. The screen therefore has to ask, and this flag is
 * how it knows to — rather than sending a merge with no target and reading the 400 back.
 */
export function transitionNeedsMergeTarget(to: CompanyLifecycleStatus): boolean {
  return to === 'merged_into';
}

const COMPANY_REFUSAL_HE: Readonly<Record<string, { readonly titleHe: string; readonly explanationHe: string }>> = {
  company_not_found: {
    titleHe: 'החברה אינה זמינה',
    explanationHe: 'לא הצלחנו להציג את החברה שאליה מפנה הפעולה.',
  },
  /*
   * RE-KEYED FROM `invalid_transition`, HEBREW PRESERVED WORD FOR WORD — X7 §3.2(b).
   *
   * The company machine answers `illegal_transition` (`services/tenant-administration.ts:169, 209`)
   * and this map had no entry for it, so the most common company-transition refusal rendered
   * `GENERIC_COMPANY_REFUSAL` while this exact sentence sat one key away, unreachable. The orphan is
   * retired by being pointed at the code the machine actually emits.
   *
   * THE SERVER IDENTIFIER IS NOT RENAMED. `illegal_transition` is the wire contract of a live route
   * and a member of an exported union; renaming it would change a response body for zero user gain.
   */
  illegal_transition: {
    titleHe: 'המעבר אינו אפשרי מהמצב הנוכחי',
    explanationHe: 'מצבי החברה עוברים במסלול קבוע, והמעבר שביקשת אינו יוצא מהמצב שבו החברה נמצאת.',
  },
  already_in_state: {
    titleHe: 'החברה כבר במצב הזה',
    explanationHe: 'המצב שביקשת הוא המצב הנוכחי של החברה, ולכן אין מה לשנות.',
  },
  merge_target_required: {
    titleHe: 'חסרה החברה הקולטת',
    explanationHe: 'סימון מיזוג מחייב לציין לאיזו חברה מוזגה החברה הזו.',
  },
  merge_target_not_found: {
    titleHe: 'החברה הקולטת אינה נמצאת בארגון',
    explanationHe: 'החברה שאליה מוזגה החברה הזו חייבת להיות חברה קיימת באותו ארגון.',
  },
};

const GENERIC_COMPANY_REFUSAL = {
  titleHe: 'מצב החברה לא שונה',
  explanationHe: 'השרת סירב לבקשה ולא נשמר שינוי.',
};

/** Exposed for the mirror test only. */
export function mappedCompanyRefusalReasons(): readonly string[] {
  return Object.keys(COMPANY_REFUSAL_HE);
}

export interface CompanyTransitionRefusalText extends LifecycleRefusalText {
  /**
   * The moves that WOULD work, from where the company actually is. Empty means the server sent none,
   * and the screen then offers nothing rather than the list it was already showing — that list was
   * computed before the refusal and is exactly what has just been proven wrong.
   */
  readonly allowedTransitions: readonly CompanyLifecycleStatus[];
}

export function companyTransitionRefusalText(
  refusal: CompanyTransitionRefusal,
  lifecycleLabelHe: (status: CompanyLifecycleStatus) => string,
): CompanyTransitionRefusalText {
  const copy = COMPANY_REFUSAL_HE[refusal.reason] ?? GENERIC_COMPANY_REFUSAL;
  const from =
    refusal.from === null ? null : `מצב החברה כעת: ${lifecycleLabelHe(refusal.from)}.`;

  return {
    titleHe: copy.titleHe,
    explanationHe: [copy.explanationHe, from].filter((s): s is string => s !== null).join(' '),
    nextStepHe:
      refusal.allowedTransitions.length === 0
        ? 'אפשר לרענן את כרטיס החברה ולראות את מצבה העדכני לפני ניסיון נוסף.'
        : `מהמצב הנוכחי אפשר לעבור אל: ${refusal.allowedTransitions.map(lifecycleLabelHe).join(' · ')}.`,
    tone: refusal.reason === 'already_in_state' ? 'warn' : 'error',
    unchangedHe: 'לא נשמר שינוי. מצב החברה נשאר כפי שהיה לפני הבקשה.',
    allowedTransitions: refusal.allowedTransitions,
  };
}
